"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useSpring, useMotionValue } from "framer-motion";
import { useReducedMotion } from "@/hooks/useReducedMotion";

interface BlobCursorProps {
  color?: string;
  size?: number;
  opacity?: number;
}

export function BlobCursor({
  color = "#8b5cf6",
  size = 300,
  opacity = 0.3
}: BlobCursorProps) {
  const reducedMotion = useReducedMotion();
  const [isMobile, setIsMobile] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springConfig = { damping: 25, stiffness: 150 };
  const x = useSpring(mouseX, springConfig);
  const y = useSpring(mouseY, springConfig);

  useEffect(() => {
    // Check if device has mouse
    const hasPointer = window.matchMedia("(pointer: fine)").matches;
    setIsMobile(!hasPointer);

    if (!hasPointer || reducedMotion) return;

    const handleMouseMove = (e: MouseEvent) => {
      mouseX.set(e.clientX - size / 2);
      mouseY.set(e.clientY - size / 2);
      if (!isVisible) setIsVisible(true);
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    document.body.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      document.body.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [mouseX, mouseY, size, reducedMotion, isVisible]);

  if (isMobile || reducedMotion) {
    return null;
  }

  return (
    <motion.div
      className="fixed pointer-events-none z-[9999]"
      style={{
        x,
        y,
        width: size,
        height: size,
        opacity: isVisible ? opacity : 0,
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: isVisible ? opacity : 0 }}
      transition={{ duration: 0.3 }}
    >
      <div
        className="w-full h-full rounded-full"
        style={{
          background: `radial-gradient(circle, ${color}60 0%, ${color}20 40%, transparent 70%)`,
          filter: "blur(40px)",
        }}
      />
    </motion.div>
  );
}

export default BlobCursor;
